import { useEffect, useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { auth } from "../../configs/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import { listPublicActiveWalls } from "../../utils/firestore";
import { DEFAULT_PUBLIC_WALL } from "../../utils/cards/cardConstants";
import BackgroundLights from "./components/BackgroundLights";
import EmpathyPageHeader from "./components/EmpathyPageHeader";

const REFINE_DARK_BACKGROUND = "#050604";

function isPermissionDeniedError(error) {
  return error?.code === "permission-denied";
}

function useDarkDocumentBackground() {
  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");
    const previousHtmlBackground =
      document.documentElement.style.backgroundColor;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousRootBackground = rootElement?.style.backgroundColor || "";

    document.documentElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    document.body.style.backgroundColor = REFINE_DARK_BACKGROUND;

    if (rootElement) {
      rootElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor = previousHtmlBackground;
      document.body.style.backgroundColor = previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor = previousRootBackground;
      }
    };
  }, []);
}

function WallListItem({ wall }) {
  return (
    <Link
      to={`/wall/${wall.id}`}
      className="group block rounded-2xl border border-[#f4d94e]/12 bg-white/[0.055] px-5 py-5 text-left text-white no-underline shadow-[0_18px_46px_rgba(0,0,0,0.24)] backdrop-blur-xl transition hover:border-[#f4d94e]/28 hover:bg-white/[0.075] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] sm:px-6 sm:py-6"
    >
      {wall.eyebrow ? (
        <p className="font-sans text-[11px] font-black uppercase text-[#f4d94e]">
          {wall.eyebrow}
        </p>
      ) : null}

      <div className="mt-3 flex items-center justify-between gap-4">
        <h2 className="font-serif text-3xl font-medium leading-[0.96] text-white sm:text-4xl">
          {wall.title || "Community Wall"}
        </h2>

        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f4d94e]/18 bg-[#f4d94e]/10 text-[#fff6b8] transition group-hover:bg-[#f4d94e]/16"
          aria-hidden="true"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 20 20"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m8 4 6 6-6 6" />
            <path d="M13 10H4" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

function WallsRouteState({ title, message }) {
  return (
    <div className="rounded-2xl border border-[#f4d94e]/12 bg-white/[0.055] px-5 py-7 text-center text-white shadow-[0_18px_46px_rgba(0,0,0,0.24)] backdrop-blur-xl">
      <h2 className="font-serif text-3xl font-medium leading-tight">
        {title}
      </h2>
      <p className="mx-auto mt-3 max-w-sm font-sans text-sm font-semibold leading-relaxed text-white/62">
        {message}
      </p>
    </div>
  );
}

function WallsView() {
  const { currentUser } = useAuth();
  const [walls, setWalls] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const activeUser = auth.currentUser || currentUser;

  useDarkDocumentBackground();

  useEffect(() => {
    let isMounted = true;

    const loadWalls = async () => {
      try {
        const publicWalls = await listPublicActiveWalls();

        if (!isMounted) {
          return;
        }

        setWalls(publicWalls);
        setLoadState("loaded");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error("Unable to load community walls", error);

        if (isPermissionDeniedError(error)) {
          setWalls([DEFAULT_PUBLIC_WALL]);
          setLoadState("loaded");
          return;
        }

        setWalls([]);
        setLoadState("error");
      }
    };

    loadWalls();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div
      className="min-h-screen min-h-[100dvh] overflow-x-hidden font-serif text-white"
      style={{ backgroundColor: REFINE_DARK_BACKGROUND }}
    >
      <Helmet>
        <meta name="theme-color" content={REFINE_DARK_BACKGROUND} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Helmet>

      <main className="relative min-h-screen overflow-hidden px-5 pb-24 pt-8 sm:px-8 sm:pt-12">
        <div className="pointer-events-none absolute inset-x-[-12%] bottom-[-18vh] z-0 h-[54vh] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(244,217,78,0.34)_0%,rgba(106,174,37,0.18)_34%,rgba(255,255,255,0.08)_54%,transparent_74%)] blur-3xl" />

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 mx-auto h-[60vh] max-w-3xl overflow-visible opacity-80">
          <BackgroundLights />
        </div>

        <section className="relative z-10 mx-auto max-w-3xl">
          <EmpathyPageHeader
            title="Community Walls"
            authUser={activeUser}
            showBackButton={false}
            className="font-sans"
          />

          <div className="mx-auto mt-10 max-w-2xl text-center sm:mt-14">
            <h1 className="text-4xl font-medium leading-[0.95] text-white sm:text-6xl">
              Community Walls
            </h1>
            <p className="mx-auto mt-4 max-w-xl font-sans text-sm font-semibold leading-relaxed text-white/66 sm:text-base">
              Public walls from LettuceTalk communities.
            </p>
          </div>

          <div className="mt-8 space-y-4 sm:mt-10">
            {loadState === "loading" ? (
              <WallsRouteState
                title="Opening community walls..."
                message="Loading public walls."
              />
            ) : null}

            {loadState === "error" ? (
              <WallsRouteState
                title="Community walls aren't available right now."
                message="Please try again in a moment."
              />
            ) : null}

            {loadState === "loaded" && walls.length === 0 ? (
              <WallsRouteState
                title="No community walls are available yet."
                message="Public walls will appear here when they are active."
              />
            ) : null}

            {loadState === "loaded"
              ? walls.map((wall) => <WallListItem key={wall.id} wall={wall} />)
              : null}
          </div>
        </section>
      </main>
    </div>
  );
}

export default WallsView;
